import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Column } from 'simple-flexbox';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Paper,
    TextField,
    Box,
    Pagination,
    Chip
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { IconSearch } from 'assets/icons';
import { useReactToPrint } from 'react-to-print';
import axios from '../../axios';
import Spinner from '../../components/Spinner';
import AddPackageModel from '../../components/AddPackageModel';
import { formatNumber } from 'config';
import PageHeader from 'components/PageHeader';

const useStyles = createUseStyles({
    container: {
        backgroundColor: '#ffffff',
        minHeight: '100vh'
    },
    headerSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: '#F9FAFB',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 8px 8px rgba(0, 0, 0, 0.15)',
        '@media print': {
            display: 'none !important'
        }
    },
    pageTitle: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#1f2937',
        margin: 0
    },
    contentSection: {
        width: '100%'
    },
    statsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: '16px'
        },
        '@media print': {
            display: 'none !important'
        }
    },
    statCard: {
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
        }
    },
    statCardDebt: {
        background: 'linear-gradient(135deg, #fef7f7 0%, #fef2f2 100%)',
        borderColor: '#fecaca',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(220, 38, 38, 0.15)'
        }
    },
    statCardPaid: {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderColor: '#bbf7d0',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(34, 197, 94, 0.15)'
        }
    },
    statCardTotal: {
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderColor: '#bfdbfe',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(59, 130, 246, 0.15)'
        }
    },
    printTitle: {
        '@media print': {
            display: 'block !important',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#1f2937'
        },
        '@media screen': {
            display: 'none'
        }
    }
});

export default function Packages() {
    const classes = useStyles();
    const compRef = useRef();

    // State management
    const [packages, setPackages] = useState([]);
    const [allPackages, setAllPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchWord, setSearchWord] = useState('');
    const [products, setProducts] = useState([]);
    const [prodError, setProdError] = useState('');

    // Statistics
    const [totalPackages, setTotalPackages] = useState(0);
    const [totalPaid, setTotalPaid] = useState(0);
    const [totalDebt, setTotalDebt] = useState(0);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Fetch packages
    const fetchPackages = useCallback(() => {
        setLoading(true);
        axios
            .get('/packages')
            .then((response) => {
                const packagesData = response.data.data || [];
                setPackages(packagesData);
                setAllPackages(packagesData);
                computeTotalAmount(packagesData);
                setLoading(false);
            })
            .catch(() => {
                setPackages([]);
                setAllPackages([]);
                setLoading(false);
            });
    }, []);

    // Fetch products
    const fetchProducts = useCallback(() => {
        axios
            .get('/products')
            .then((response) => {
                setProducts(response.data.data || []);
            })
            .catch(() => {
                setProdError('Failed to fetch products');
            });
    }, []);

    // Initialize data
    useEffect(() => {
        fetchPackages();
        fetchProducts();
    }, [fetchPackages, fetchProducts]);

    // Search functionality
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchWord(value);
        setCurrentPage(1);

        if (value.trim() === '') {
            setPackages(allPackages);
        } else {
            const filtered = allPackages.filter((pkg) =>
                pkg.name.toLowerCase().includes(value.toLowerCase())
            );
            setPackages(filtered);
        }
    };

    const handleSearchClick = () => {
        // Search is handled in real-time via handleSearchChange
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick();
        }
    };

    // Calculate totals
    const computeTotalAmount = (data) => {
        if (!data || data.length === 0) {
            setTotalPackages(0);
            setTotalPaid(0);
            setTotalDebt(0);
            return;
        }

        const totalAmount = data.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
        const totalAmountPaid = data.reduce((sum, item) => sum + (item.balance || 0), 0);

        setTotalPackages(totalAmount);
        setTotalPaid(totalAmountPaid);
        setTotalDebt(totalAmount - totalAmountPaid);
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPackages = packages.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(packages.length / itemsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // Print functionality
    const PrintUserPackages = useReactToPrint({
        content: () => compRef.current
    });

    // Table styling
    const tableCellStyle = {
        fontWeight: 600,
        fontSize: '12px',
        color: '#ffffff',
        textTransform: 'uppercase',
        padding: '12px 16px'
    };

    const customTableCellStyle = {
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#374151',
        borderBottom: '1px solid #f3f4f6',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    return (
        <div className={classes.container}>
            <Column>
                {/* Header Section */}

                <PageHeader
                    title='Packages'
                    subtitle='Manage your platform packages efficiently'
                    variant='gradient'
                />
                {/* Header Section */}
                <div className={classes.headerSection}>
                    <Button
                        variant='contained'
                        startIcon={<Print />}
                        onClick={PrintUserPackages}
                        sx={{
                            backgroundColor: '#085d40',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#064532'
                            }
                        }}
                    >
                        Print Report
                    </Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <TextField
                            required
                            id='package-search'
                            label='Search Packages'
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        borderColor: '#d1d5db'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#085d40'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#085d40'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280'
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#085d40'
                                }
                            }}
                            placeholder='Package name'
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            value={searchWord}
                        />

                        <div
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#085d40',
                                color: '#ffffff',
                                padding: '10px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #085d40'
                            }}
                            onClick={handleSearchClick}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#064532';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#085d40';
                            }}
                        >
                            <IconSearch color='#ffffff' />
                        </div>
                    </div>
                </div>

                <div className={classes.contentSection}>
                    {/* Enhanced Statistics Cards */}
                    {packages.length > 0 && (
                        <div className={classes.statsContainer}>
                            {/* Total Debt Card */}
                            <div className={`${classes.statCard} ${classes.statCardDebt}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <h3
                                            style={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                margin: 0,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Debt
                                        </h3>
                                    </div>
                                    <Chip
                                        label='Outstanding'
                                        size='small'
                                        sx={{
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <div>
                                    <p
                                        style={{
                                            color: '#dc2626',
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            margin: 0,
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {totalDebt.toLocaleString('en-US')}
                                    </p>
                                    <p
                                        style={{
                                            color: '#6b7280',
                                            fontSize: '13px',
                                            margin: '4px 0 0 0',
                                            fontWeight: 500
                                        }}
                                    >
                                        UGX Outstanding
                                    </p>
                                </div>
                            </div>

                            {/* Total Paid Card */}
                            <div className={`${classes.statCard} ${classes.statCardPaid}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <h3
                                            style={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                margin: 0,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Paid
                                        </h3>
                                    </div>
                                    <Chip
                                        label='Received'
                                        size='small'
                                        sx={{
                                            backgroundColor: '#dcfce7',
                                            color: '#059669',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <div>
                                    <p
                                        style={{
                                            color: '#059669',
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            margin: 0,
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {totalPaid.toLocaleString('en-US')}
                                    </p>
                                    <p
                                        style={{
                                            color: '#6b7280',
                                            fontSize: '13px',
                                            margin: '4px 0 0 0',
                                            fontWeight: 500
                                        }}
                                    >
                                        UGX Collected
                                    </p>
                                </div>
                            </div>

                            {/* Total Packages Value Card */}
                            <div className={`${classes.statCard} ${classes.statCardTotal}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <h3
                                            style={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                margin: 0,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Value
                                        </h3>
                                    </div>
                                    <Chip
                                        label={`${packages.length} Packages`}
                                        size='small'
                                        sx={{
                                            backgroundColor: '#dbeafe',
                                            color: '#3b82f6',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <div>
                                    <p
                                        style={{
                                            color: '#3b82f6',
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            margin: 0,
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {totalPackages.toLocaleString('en-US')}
                                    </p>
                                    <p
                                        style={{
                                            color: '#6b7280',
                                            fontSize: '13px',
                                            margin: '4px 0 0 0',
                                            fontWeight: 500
                                        }}
                                    >
                                        UGX Total Value
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {loading ? (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '60px'
                            }}
                        >
                            <Spinner size='big' />
                        </div>
                    ) : (
                        <>
                            {/* Table Container for Print */}
                            <div ref={compRef}>
                                {/* Print Title - Only visible when printing */}
                                <div className={classes.printTitle}>
                                    <h1 style={{ margin: '20px 0' }}>Packages Report</h1>
                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            margin: '10px 0'
                                        }}
                                    >
                                        Generated on{' '}
                                        {new Date().toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>

                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                        overflow: 'auto',
                                        maxWidth: '100%',
                                        '@media print': {
                                            boxShadow: 'none',
                                            border: '1px solid #000',
                                            borderRadius: 0,
                                            overflow: 'visible'
                                        }
                                    }}
                                >
                                    {/* Table Header */}
                                    {/* <Box
                                        sx={{
                                            padding: '20px 24px',
                                            backgroundColor: '#f8fafc',
                                            borderBottom: '1px solid #e2e8f0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div>
                                            <h2
                                                style={{
                                                    color: '#1f2937',
                                                    fontWeight: 600,
                                                    margin: 0,
                                                    fontSize: '18px'
                                                }}
                                            >
                                                Packages ({packages.length})
                                            </h2>
                                        </div>
                                    </Box> */}

                                    <Table
                                        sx={{
                                            minWidth: 650,
                                            width: '100%',
                                            tableLayout: 'fixed'
                                        }}
                                        aria-label='packages table'
                                    >
                                        <TableHead
                                            sx={{
                                                backgroundColor: '#085d40',
                                                borderBottom: '2px solid #064532'
                                            }}
                                        >
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '5%'
                                                    }}
                                                >
                                                    #
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '18%'
                                                    }}
                                                >
                                                    Package Name
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '12%'
                                                    }}
                                                >
                                                    Date Created
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '10%'
                                                    }}
                                                >
                                                    Status
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '12%'
                                                    }}
                                                >
                                                    Amount Paid
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '12%'
                                                    }}
                                                >
                                                    Total Amount
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '21%'
                                                    }}
                                                >
                                                    Products
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyle,
                                                        width: '10%'
                                                    }}
                                                >
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {currentPackages.length > 0 ? (
                                                currentPackages.map((row, index) => (
                                                    <TableRow
                                                        key={row._id || index}
                                                        sx={{
                                                            backgroundColor:
                                                                index % 2 === 0
                                                                    ? '#ffffff'
                                                                    : '#f9fafb',
                                                            '&:hover': {
                                                                backgroundColor: '#f3f4f6'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={customTableCellStyle}>
                                                            {row.number ||
                                                                indexOfFirstItem + index + 1}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                ...customTableCellStyle,
                                                                fontWeight: 600,
                                                                color: '#1f2937',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            title={row.name}
                                                        >
                                                            {row.name}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                ...customTableCellStyle,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        >
                                                            {new Date(
                                                                row.createdAt
                                                            ).toLocaleDateString('en-US')}
                                                        </TableCell>
                                                        <TableCell sx={customTableCellStyle}>
                                                            <Chip
                                                                label={row.status}
                                                                size='small'
                                                                sx={{
                                                                    backgroundColor:
                                                                        row.status === 'completed'
                                                                            ? '#ecfdf5'
                                                                            : '#fef3c7',
                                                                    color:
                                                                        row.status === 'completed'
                                                                            ? '#059669'
                                                                            : '#92400e',
                                                                    border: `1px solid ${
                                                                        row.status === 'completed'
                                                                            ? '#10b981'
                                                                            : '#f59e0b'
                                                                    }`,
                                                                    fontWeight: 600
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                ...customTableCellStyle,
                                                                fontFamily: 'monospace',
                                                                color: '#065f46',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {formatNumber(row.balance)} UGX
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                ...customTableCellStyle,
                                                                fontFamily: 'monospace',
                                                                color: '#1f2937',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {formatNumber(row.totalAmount)} UGX
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                ...customTableCellStyle,
                                                                whiteSpace: 'normal',
                                                                wordWrap: 'break-word',
                                                                lineHeight: '1.4'
                                                            }}
                                                        >
                                                            {row.products?.length > 0
                                                                ? row.products
                                                                      .map(
                                                                          (prod) =>
                                                                              `${prod.count}x ${prod.name}`
                                                                      )
                                                                      .join(', ')
                                                                : 'No products'}
                                                        </TableCell>
                                                        <TableCell sx={customTableCellStyle}>
                                                            <AddPackageModel
                                                                products={products}
                                                                selectedPackage={row}
                                                                isEditMode={true}
                                                                prodError={prodError}
                                                                farmerId={null}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={8}
                                                        sx={{
                                                            textAlign: 'center',
                                                            padding: '40px',
                                                            color: '#6b7280',
                                                            fontSize: '16px'
                                                        }}
                                                    >
                                                        {searchWord
                                                            ? `No packages found matching "${searchWord}"`
                                                            : 'No packages available'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: '20px',
                                        padding: '20px',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                                    }}
                                >
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color='primary'
                                        size='large'
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                color: '#1f2937',
                                                borderColor: '#e2e8f0',
                                                '&:hover': {
                                                    backgroundColor: '#f3f4f6'
                                                }
                                            },
                                            '& .MuiPaginationItem-page.Mui-selected': {
                                                backgroundColor: '#085d40',
                                                color: '#ffffff'
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Column>
        </div>
    );
}
